import React, { createContext, useContext, useReducer, useState, ReactNode } from "react";
import {
  Exercise as UIExercise,
  ExerciseType,
  MultipleChoiceData,
  isMultipleChoiceData,
} from "@/components/exercises/exercise.interfaces";
import {
  apiGetModuleTestPool,
  apiPostModuleTestPoolExercise,
  apiPutModuleTestPoolExercise,
  apiDeleteModuleTestPoolExercise,
  apiPutModuleTestPoolExercisesOrder,
} from "@/services/admin/module-test-pool.api";
import { useAppToast } from "@/hooks/useAppToast";

interface ExerciseState {
  exercises: UIExercise[];
  isRefreshing: boolean;
  error: string | null;
  poolSizeBelowRecommended?: boolean;
}

enum ActionType {
  FETCH_START = "FETCH_START",
  FETCH_SUCCESS = "FETCH_SUCCESS",
  FETCH_ERROR = "FETCH_ERROR",
  ADD_EXERCISE = "ADD_EXERCISE",
  UPDATE_EXERCISE = "UPDATE_EXERCISE",
  DELETE_EXERCISE = "DELETE_EXERCISE",
  SET_EXERCISES = "SET_EXERCISES",
  SORT_START = "SORT_START",
  SORT_SUCCESS = "SORT_SUCCESS",
  SORT_ERROR = "SORT_ERROR",
}

type Action =
  | { type: ActionType.FETCH_START }
  | { type: ActionType.FETCH_SUCCESS; payload: { exercises: UIExercise[]; poolSizeBelowRecommended?: boolean } }
  | { type: ActionType.FETCH_ERROR; payload: string }
  | { type: ActionType.ADD_EXERCISE; payload: UIExercise }
  | { type: ActionType.UPDATE_EXERCISE; payload: UIExercise }
  | { type: ActionType.DELETE_EXERCISE; payload: string }
  | { type: ActionType.SET_EXERCISES; payload: UIExercise[] }
  | { type: ActionType.SORT_START }
  | { type: ActionType.SORT_SUCCESS; payload: UIExercise[] }
  | { type: ActionType.SORT_ERROR; payload: string };

const reducer = (state: ExerciseState, action: Action): ExerciseState => {
  switch (action.type) {
    case ActionType.FETCH_START:
    case ActionType.SORT_START:
      return { ...state, isRefreshing: true, error: null };
    case ActionType.FETCH_SUCCESS:
      return {
        ...state,
        exercises: action.payload.exercises,
        isRefreshing: false,
        error: null,
        poolSizeBelowRecommended: action.payload.poolSizeBelowRecommended,
      };
    case ActionType.FETCH_ERROR:
    case ActionType.SORT_ERROR:
      return { ...state, isRefreshing: false, error: action.payload };
    case ActionType.ADD_EXERCISE:
      return { ...state, exercises: [...state.exercises, action.payload] };
    case ActionType.UPDATE_EXERCISE:
      return {
        ...state,
        exercises: state.exercises.map((e) =>
          e.id === action.payload.id ? action.payload : e
        ),
      };
    case ActionType.DELETE_EXERCISE:
      return {
        ...state,
        exercises: state.exercises.filter((e) => String(e.id) !== action.payload),
      };
    case ActionType.SET_EXERCISES:
      return { ...state, exercises: action.payload };
    case ActionType.SORT_SUCCESS:
      return { ...state, exercises: action.payload, isRefreshing: false, error: null };
    default:
      return state;
  }
};

const typeMapping: Record<string, string> = {
  MultipleChoice: "multiple_choice",
  multiple_choice: "multiple_choice",
};

function mapBackendToUI(item: { id: string; type: string; data?: unknown; order: number }): UIExercise {
  let data = item.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      data = {};
    }
  }
  const type = typeMapping[item.type] || item.type;
  return {
    id: item.id,
    type: type as UIExercise["type"],
    data: data ?? {},
    order: item.order,
  } as UIExercise;
}

interface ModuleTestPoolExerciseContextType {
  moduleId: string;
  state: ExerciseState;
  refreshExercises: () => Promise<boolean>;
  addExercise: (exercise: UIExercise) => Promise<boolean>;
  updateExercise: (exercise: UIExercise) => Promise<boolean>;
  deleteExercise: (id: string) => Promise<boolean>;
  batchUpdateExercisesSortOrder: (newOrder: (string | number)[]) => Promise<boolean>;
  dialogOpen: boolean;
  editingExercise?: UIExercise;
  openDialog: (exercise?: UIExercise) => void;
  closeDialog: () => void;
}

const ModuleTestPoolExerciseContext = createContext<ModuleTestPoolExerciseContextType | undefined>(undefined);

export const useModuleTestPoolExerciseContext = () => {
  const ctx = useContext(ModuleTestPoolExerciseContext);
  if (ctx === undefined) {
    throw new Error("useModuleTestPoolExerciseContext must be used within ModuleTestPoolExerciseProvider");
  }
  return ctx;
};

interface ProviderProps {
  children: ReactNode;
  moduleId: string;
}

export const ModuleTestPoolExerciseProvider: React.FC<ProviderProps> = ({ children, moduleId }) => {
  const toast = useAppToast();
  const [state, dispatch] = useReducer(reducer, {
    exercises: [],
    isRefreshing: false,
    error: null,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState<UIExercise | undefined>();

  const refreshExercises = async (): Promise<boolean> => {
    try {
      dispatch({ type: ActionType.FETCH_START });
      const res = await apiGetModuleTestPool(moduleId);
      const exercises = (res.exercises ?? []).map(mapBackendToUI);
      dispatch({
        type: ActionType.FETCH_SUCCESS,
        payload: {
          exercises,
          poolSizeBelowRecommended: res.poolSizeBelowRecommended,
        },
      });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Falha ao carregar pool";
      dispatch({ type: ActionType.FETCH_ERROR, payload: msg });
      toast.error(msg);
      return false;
    }
  };

  /** Build AnswerData for multiple-choice: array of option ids (as numbers) for options marked correct. */
  function getAnswerDataFromExercise(exercise: UIExercise): number[] | undefined {
    if (!isMultipleChoiceData(exercise.data)) return undefined;
    const ids = (exercise.data.options ?? [])
      .filter((o) => o.isCorrect)
      .map((o) => parseInt(o.id, 10))
      .filter((n) => !Number.isNaN(n));
    return ids.length > 0 ? ids : undefined;
  }

  const addExercise = async (exercise: UIExercise): Promise<boolean> => {
    try {
      const answerData = getAnswerDataFromExercise(exercise);
      const payload = {
        type: exercise.type,
        data: exercise.data,
        title: (exercise.data as { question?: string })?.question?.substring(0, 200) || "Exercise",
        ...(answerData != null && { answerData }),
      };
      await apiPostModuleTestPoolExercise(moduleId, payload);
      await refreshExercises();
      toast.success("Exercício adicionado.");
      return true;
    } catch {
      toast.error("Não foi possível adicionar o exercício.");
      return false;
    }
  };

  const updateExercise = async (exercise: UIExercise): Promise<boolean> => {
    if (!exercise.id) return false;
    const id = String(exercise.id);
    try {
      const answerData = getAnswerDataFromExercise(exercise);
      const payload = {
        type: exercise.type,
        data: exercise.data,
        title: (exercise.data as { question?: string })?.question?.substring(0, 200) || "Exercise",
        // Backend persists AnswerData when provided; required for multiple-choice correction.
        ...(answerData != null && { answerData }),
      };
      await apiPutModuleTestPoolExercise(moduleId, id, payload);
      await refreshExercises();
      toast.success("Exercício atualizado.");
      return true;
    } catch {
      toast.error("Não foi possível atualizar o exercício.");
      return false;
    }
  };

  const deleteExercise = async (id: string): Promise<boolean> => {
    try {
      await apiDeleteModuleTestPoolExercise(moduleId, id);
      await refreshExercises();
      toast.success("Exercício removido do pool.");
      return true;
    } catch {
      toast.error("Não foi possível remover o exercício.");
      return false;
    }
  };

  const batchUpdateExercisesSortOrder = async (newOrder: (string | number)[]): Promise<boolean> => {
    try {
      dispatch({ type: ActionType.SORT_START });
      await apiPutModuleTestPoolExercisesOrder(moduleId, newOrder.map(String));
      await refreshExercises();
      toast.success("Ordem atualizada.");
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro ao atualizar ordem";
      dispatch({ type: ActionType.SORT_ERROR, payload: msg });
      toast.error(msg);
      return false;
    }
  };

  const openDialog = (exercise?: UIExercise) => {
    if (exercise?.id) {
      let data = exercise.data;
      if (isMultipleChoiceData(data)) {
        setEditingExercise({
          ...exercise,
          type: ExerciseType.MultipleChoice,
          data: { ...data },
        } as UIExercise);
      } else {
        setEditingExercise({ ...exercise });
      }
    } else {
      setEditingExercise({
        type: ExerciseType.MultipleChoice,
        data: {
          question: "",
          options: [{ id: "1", text: "", isCorrect: false }],
          multipleCorrectAnswers: false,
        } as MultipleChoiceData,
      } as UIExercise);
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingExercise(undefined);
  };

  const value: ModuleTestPoolExerciseContextType = {
    moduleId,
    state,
    refreshExercises,
    addExercise,
    updateExercise,
    deleteExercise,
    batchUpdateExercisesSortOrder,
    dialogOpen,
    editingExercise,
    openDialog,
    closeDialog,
  };

  return (
    <ModuleTestPoolExerciseContext.Provider value={value}>
      {children}
    </ModuleTestPoolExerciseContext.Provider>
  );
};
